import React from 'react'
import InputDeviceSelector from '@bt-react/components/InputDeviceSelector'
import ConfirmModal from '@bt-react/components/ConfirmModal'
import settingsModalStyles from './SettingsModal.module.scss'
import { SettingsModalProps, SettingsModalState, SettingsTabs } from './types'
import AudioSelector from '../AudioSelector'
import { DeviceStats } from '@bt-react/libs/devices'
import get from 'lodash/get'
import { AudioSource } from '../../dataStore/reducers/tracks/tracksReducerDefaultState'
import { applyBackground } from '../../lib/virtualBackground'
import browser from '@bt-react/libs/browser'
import Tabs from '@bt-react/components/Tabs'
import VirtualBackgroundPicker from '../VirtualBackgroundPicker'
import { VirtualBackgrounds } from '../VirtualBackgroundPicker/VirtualBackgroundPicker'
import classNames from 'classnames'
import { get720FromCustomImage } from '../../lib/customVirtualBackground'
import audit from '../../lib/audit/audit'


class SettingsModal extends React.Component<SettingsModalProps, SettingsModalState> {

  constructor (props) {

    super(props)
    this.state = {
      selectedWebcam: undefined,
      selectedMicrophone: undefined,
      selectedAudioSource: this.props.audioSource,
      videoTrack: null,
      selectedTab: this.props.selectedTab,
      backgroundType: this.props.background ? this.props.background.type : VirtualBackgrounds.NONE
    }

  }

  componentDidMount () {
    audit.addEntry('PresenterService', 'SystemSettingsDisplayed', JSON.stringify({
      localTimestamp: Date.now().toString(),
      cookies: document.cookie,
      browser: {
        vendor: navigator.vendor,
        userAgent: navigator.userAgent
      },
      state: {...this.state, videoTrack: !!this.state.videoTrack}
    }))
  }

  componentDidUpdate (prevProps) {

    if (this.props.isDialInConnected &&
      this.props.isDialInConnected !== prevProps.isDialInConnected) this.onModalConfirm()

  }

  componentWillUnmount() {
    if (this.state.videoTrack && this.state.backgroundType !== VirtualBackgrounds.NONE) {
      applyBackground(this.state.videoTrack, {customImage: null, type: VirtualBackgrounds.NONE})
    }
  }

  onWebcamChange = (selectedWebcam: DeviceStats): void => {
    this.setState({ selectedWebcam })
    audit.addEntry('PresenterService', 'SystemSettingsWebcamChange', JSON.stringify({
      localTimestamp: Date.now().toString(),
      cookies: document.cookie,
      browser: {
        vendor: navigator.vendor,
        userAgent: navigator.userAgent
      },
      state: {...this.state, videoTrack: !!this.state.videoTrack},
      selectedWebcam: selectedWebcam

    }))
    
  }
  onMicrophoneChange = (selectedMicrophone: DeviceStats): void => {
    this.setState({ selectedMicrophone })
    audit.addEntry('PresenterService', 'SystemSettingsMicrophoneChange', JSON.stringify({
      localTimestamp: Date.now().toString(),
      cookies: document.cookie,
      browser: {
        vendor: navigator.vendor,
        userAgent: navigator.userAgent
      },
      state: {...this.state, videoTrack: !!this.state.videoTrack},
      selectedMicrophone: selectedMicrophone

    }))
  }
  onAudioSourceChange = (selectedAudioSource: AudioSource): void => {
    this.setState({ selectedAudioSource })
    audit.addEntry('PresenterService', 'SystemSettingsAudioSourceChange', JSON.stringify({
      localTimestamp: Date.now().toString(),
      cookies: document.cookie,
      browser: {
        vendor: navigator.vendor,
        userAgent: navigator.userAgent
      },
      state: {...this.state, videoTrack: !!this.state.videoTrack},
      selectedAudioSource: selectedAudioSource

    }))
  }

  onVideoTrackLoad = (videoTrack) => {
    const isChrome = browser.isChrome()
    const customImage720 = get720FromCustomImage(this.props.background.customImage || '')
    const background = this.state.backgroundType ? {type: this.state.backgroundType, customImage: customImage720} : this.props.background
    this.setState({videoTrack})

    if (videoTrack !== null && isChrome && background) {
      applyBackground(videoTrack, background)
    }
  }


  onBackgroundSelect = (backgroundType: VirtualBackgrounds) => {
    if (this.state.videoTrack) {
      const customImage720 = get720FromCustomImage(this.props.background.customImage || '')
      applyBackground(this.state.videoTrack, {
        type: backgroundType,
        customImage: customImage720
      })
    }

    this.setState({
      backgroundType
    })
    audit.addEntry('PresenterService', 'SystemSettingsBackgroundChange', JSON.stringify({
      localTimestamp: Date.now().toString(),
      cookies: document.cookie,
      browser: {
        vendor: navigator.vendor,
        userAgent: navigator.userAgent
      },
      state: {...this.state, videoTrack: !!this.state.videoTrack},
      changeBackground: backgroundType
    }))
  }

  onTabClick = (tab: SettingsTabs) => {
    this.setState({
      selectedTab: tab
    })
    audit.addEntry('PresenterService', 'SystemSettingsTabChange', JSON.stringify({
      localTimestamp: Date.now().toString(),
      cookies: document.cookie,
      browser: {
        vendor: navigator.vendor,
        userAgent: navigator.userAgent
      },
      state: {...this.state, videoTrack: !!this.state.videoTrack},
      tab: tab
    }))
  }

  getBody = () => {

    const isChrome = browser.isChrome()
    const tabs = [
      {
        tabItem: <div onClick={() => this.onTabClick(SettingsTabs.DeviceSettings)}>Device settings</div>,
        isActive: this.state.selectedTab === SettingsTabs.DeviceSettings,
        dataBdd: 'tab-device-settings'
      }
    ]

    if (isChrome) {
      tabs.push({
        tabItem: <div onClick={() => this.onTabClick(SettingsTabs.VirtualBackgrounds)}>Virtual backgrounds</div>,
        isActive: this.state.selectedTab === SettingsTabs.VirtualBackgrounds,
        dataBdd: 'tab-virtual-backgrounds'
      })
    }
    return (
      <div>
        <div data-bdd='settings-intro' className={settingsModalStyles['info']}>{'Adjust video and audio settings for your talk.'}</div>
        <Tabs tabs={tabs}/>
        <div className={settingsModalStyles['tab-container']}>
          <div className={this.state.selectedTab === SettingsTabs.DeviceSettings ? settingsModalStyles['active-tab'] : ''}>
            <div>
              <InputDeviceSelector onVideoTrackLoad={this.onVideoTrackLoad} deviceType='camera' selectedDeviceId={get(this.state.selectedWebcam, 'deviceId', this.props.selectedWebcamDeviceId)} label="Camera" onDeviceChange={this.onWebcamChange} onListLoaded={() => {}} />
            </div>
            <AudioSelector
              audioSource={this.state.selectedAudioSource}
              onAudioSourceChange={this.onAudioSourceChange}
              onMicrophoneChange={this.onMicrophoneChange}
              selectedMicrophoneDeviceId={get(this.state.selectedMicrophone, 'deviceId', this.props.selectedMicrophoneDeviceId)}
              isDialInConnected={this.props.isDialInConnected}
            />
        </div>
        <div className={classNames({
          [settingsModalStyles['active-tab']]: this.state.selectedTab === SettingsTabs.VirtualBackgrounds,
          [settingsModalStyles['backgrounds-tab']]: true
        })}>
          {isChrome && <VirtualBackgroundPicker videoTrack={this.state.videoTrack} presenterBackground={{customImage: this.props.background.customImage, type: this.state.backgroundType || this.props.background.type }} onBackgroundSelect={this.onBackgroundSelect} />}
        </div>
        </div>
      </div>
    )

  }

  onModalConfirm = () => {

    const { selectedWebcam, selectedMicrophone, backgroundType } = this.state
    const { audioSource, onConfirm } = this.props

    if (onConfirm) {

      onConfirm({ selectedWebcam, selectedMicrophone, audioSource: this.props.isDialInConnected
        && audioSource === 'telephone' ? 'telephone' : 'microphone',
        background: backgroundType
      
      })

    }
    audit.addEntry('PresenterService', 'SystemSettingsConfirm', JSON.stringify({
      localTimestamp: Date.now().toString(),
      cookies: document.cookie,
      browser: {
        vendor: navigator.vendor,
        userAgent: navigator.userAgent
      },
      state: {...this.state, videoTrack: !!this.state.videoTrack},
      audioSource: this.props.isDialInConnected
    }))

  }

  onModalCancel = () => {
    const { onCancel } = this.props
    if (onCancel) {
      onCancel()
    }
    audit.addEntry('PresenterService', 'SystemSettingsCancel', JSON.stringify({
      localTimestamp: Date.now().toString(),
      cookies: document.cookie,
      browser: {
        vendor: navigator.vendor,
        userAgent: navigator.userAgent
      },
      state: {...this.state, videoTrack: !!this.state.videoTrack},
      audioSource: this.props.isDialInConnected
    }))
    
  }

  render () {

    return <ConfirmModal
      content={{
        header: 'Presenting options',
        body: this.getBody(),
        confirm: 'OK',
        cancel: 'Cancel'
      }}
      onConfirm={this.onModalConfirm}
      onCancel={this.onModalCancel}
      isOpen={true}
      footerItems={
        <div className={settingsModalStyles['info-system-test']}>Experiencing difficulties?&nbsp;
          <a data-bdd='system-test-link' target='_blank' href='/talks-presenter-systemtest'>
            Test your system
          </a>
        </div>
      }
      className={settingsModalStyles['settings-modal']}
    />

  }

}

export default SettingsModal
